/**
 * SoftDeletePromptDialog — destructive-confirm gate for the prompts
 * recycle-bin move (`POST /api/platform/prompts/:slug/soft-delete` body
 * `{reason}`).
 *
 * Soft-delete is RECOVERABLE — it stamps `_deleted` on the slug + atomically
 * clears any pre-existing `_disabled` label, but every version row is
 * preserved and `restore` brings the slug back. Still, it hides the slug
 * from production fetches (consumers see `410 Gone`), so the dialog forces
 * the operator to (1) read the consequence bullets, (2) type the exact
 * `"I UNDERSTAND"` confirmation phrase (same acknowledgement
 * `SoftDeleteOrgDialog` uses — operator muscle-memory consistency), and
 * (3) provide a 3-500 char audited reason (forwarded to operationalLogs).
 * Confirm is gated on all three.
 *
 * Code-owned (locked / `source: "code"`) prompts surface an extra warning:
 * the boot seed honors the recycle-bin state, so a deleted code-source
 * slug will NOT be re-seeded on restart until restored.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASON_MIN = 3;
const REASON_MAX = 500;
const CONFIRM_PHRASE = "I UNDERSTAND";

export interface SoftDeletePromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  /** Provenance of the latest version — drives the locked-prompt warning. */
  source: "code" | "operator";
  /** True when the slug carries a `_disabled` label at delete time — the
   *  dialog calls out that softDelete will clear it (mutual exclusivity). */
  wasDisabled: boolean;
  onConfirm: (reason: string) => Promise<void> | void;
  busy?: boolean;
  error?: string | null;
}

export function SoftDeletePromptDialog({
  open,
  onOpenChange,
  slug,
  source,
  wasDisabled,
  onConfirm,
  busy = false,
  error = null,
}: SoftDeletePromptDialogProps) {
  const [reason, setReason] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");

  useEffect(() => {
    if (!open) {
      setReason("");
      setTypedConfirm("");
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const reasonOk =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const phraseOk = typedConfirm.trim() === CONFIRM_PHRASE;
  const canSubmit = reasonOk && phraseOk && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg space-y-4"
        data-testid="prompt-soft-delete-dialog"
      >
        <DialogHeader className="items-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl text-foreground">
            <AlertTriangle className="h-6 w-6 text-critical" />
            Soft-delete prompt
          </DialogTitle>
          <DialogDescription>
            Moves{" "}
            <strong className="font-mono text-foreground">{slug}</strong>{" "}
            to the recycle bin — restorable, but disruptive for any caller.
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
          data-testid="prompt-soft-delete-warning"
        >
          <p className="font-medium">
            This hides every version of the prompt from production fetches.
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs">
            <li>
              Every version row is preserved (append-only). Restore brings
              the chain back; consumers see <strong>410 Gone</strong> until
              then.
            </li>
            <li>
              Explicit <code>?version=</code> / <code>?label=</code> fetches
              still resolve (reproducibility / debug parity with the
              disable path).
            </li>
            {wasDisabled && (
              <li>
                This slug is currently <strong>disabled</strong>. Soft-delete
                supersedes disable — the <code>_disabled</code> label is
                cleared atomically.
              </li>
            )}
            {source === "code" && (
              <li>
                ⚠ This is a <strong>code-owned</strong> prompt — even after
                a service restart it will <strong>NOT</strong> be re-seeded
                until you restore it.
              </li>
            )}
          </ul>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="prompt-soft-delete-confirm-input"
            className="text-xs font-medium text-muted"
          >
            Type{" "}
            <span className="font-mono font-semibold text-foreground">
              {CONFIRM_PHRASE}
            </span>{" "}
            to enable the action:
          </label>
          <input
            id="prompt-soft-delete-confirm-input"
            type="text"
            autoComplete="off"
            value={typedConfirm}
            onChange={(e) => setTypedConfirm(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            disabled={busy}
            data-testid="prompt-soft-delete-confirm-input"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-critical focus:ring-1 focus:ring-critical/30 disabled:opacity-60"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="prompt-soft-delete-reason"
            className="text-xs font-medium text-muted"
          >
            Reason (audited; {REASON_MIN}-{REASON_MAX} chars):
          </label>
          <textarea
            id="prompt-soft-delete-reason"
            data-testid="prompt-soft-delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this prompt being soft-deleted?"
            disabled={busy}
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        {error && (
          <div
            className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
            data-testid="prompt-soft-delete-error"
          >
            {error}
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="prompt-soft-delete-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void onConfirm(trimmedReason)}
            data-testid="prompt-soft-delete-confirm"
            className="inline-flex items-center gap-1 rounded-md bg-critical px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-critical/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {busy ? "Soft-deleting…" : "Soft-delete"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
